<template>
  <RadioButton v-bind="radioButtonProps" @change="onChange" />
</template>

<script setup lang="ts">
import type { RadioButtonChangeEvent } from "@progress/kendo-vue-inputs";
import { RadioButton } from "@progress/kendo-vue-inputs";
import { computed } from "vue";

const props = defineProps<{
  name: string;
  value: any;
  checked: boolean;
  label: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "change", event: RadioButtonChangeEvent): void;
}>();

// Kendo RadioButton does not support boolean values directly and throws a warning in the console.
// In order to support boolean values, we convert them to numbers (1 for true, 0 for false) for internal use.
// Then, on change, we convert them back to boolean if the original value was boolean.
// Check if value is boolean and convert to number for internal use
const isBooleanValue = computed(() => typeof props.value === "boolean");

const internalValue = computed(() => {
  return isBooleanValue.value ? (props.value ? 1 : 0) : props.value;
});

// Create props object with converted value
const radioButtonProps = computed(() => ({ ...props, value: internalValue.value }));

const onChange = function (...args: any[]) {
  const event = args[0] as RadioButtonChangeEvent;
  // If original value was boolean, convert the number back to boolean
  if (isBooleanValue.value && typeof event.value === "number") {
    const modifiedEvent = {
      ...event,
      value: event.value === 1,
    };
    emit("change", modifiedEvent);
  } else {
    emit("change", event);
  }
};
</script>
